import React, { useCallback, useEffect, useState } from 'react'
import {
  Box,
  Button,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { getProtocolContainer } from '../../container/protocol-module'
import { IProtocolService, PROTOCOL_SERVICE_KEY } from '../../modules/protocols'
import {
  Evaluation,
  EvaluationDetail,
  PatientProtocol,
  Protocol,
  ProtocolStatus,
} from '../../modules/protocols/models/Protocol'

const protocolService = getProtocolContainer().get<IProtocolService>(PROTOCOL_SERVICE_KEY)

type Props = {
  patientID: string
  editable: boolean
}

// Protocols applied to a patient and the outcome of their evaluations.
// Los protocolos le llegan al paciente por sus patologias (#3185), asi que
// esta pantalla es de solo lectura. Mantiene el registro de resultados de la
// #3197 y la evaluacion ad hoc.
export function PatientProtocols(props: Props) {
  const { t } = useTranslation()

  const [assigned, setAssigned] = useState<PatientProtocol[]>([])
  const [published, setPublished] = useState<Protocol[]>([])
  const [evaluations, setEvaluations] = useState<Evaluation[]>([])
  const [evaluating, setEvaluating] = useState<boolean>(false)

  const load = useCallback(() => {
    if (!props.patientID) return
    protocolService.assignmentsByPatient(props.patientID).subscribe((l) => setAssigned(l || []))
    protocolService.evaluations(props.patientID, 20).subscribe((l) => setEvaluations(l || []))
  }, [props.patientID])

  useEffect(load, [load])

  useEffect(() => {
    // only protocols in use can be assigned, same as the backend validates
    protocolService.list(ProtocolStatus.Published).subscribe((l) => setPublished(l || []))
  }, [])

  const nameOf = (protocolID: string) =>
    published.find((p) => p.id === protocolID)?.name || protocolID

  const evaluateNowHandler = () => {
    setEvaluating(true)
    protocolService.evaluateNow(props.patientID).subscribe(() => {
      setEvaluating(false)
      load()
    })
  }

  // The engine stores the detail serialised; it is summarised so a raw JSON is
  // not dumped on screen.
  const summariseDetail = (json: string): string => {
    try {
      const details: EvaluationDetail[] = JSON.parse(json)
      return details
        .map((d) => {
          const measured = d.answers?.length ? d.answers.join(', ') : (d.values || []).join(', ')
          const mark = d.met ? '✓' : '✗'
          return `${mark} ${d.parameter} ${d.operator} ${d.reference}${
            measured ? ` (${measured})` : ''
          }${d.reason ? ` — ${d.reason}` : ''}`
        })
        .join(' · ')
    } catch {
      return json
    }
  }

  return (
    <Paper
      elevation={0}
      sx={{
        mt: 2,
        p: '22.5px 25px 12.3px 25px',
        backgroundColor: 'var(--white)',
        borderRadius: '32px',
        border: '1px solid var(--green-primary)',
        boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
      }}
    >
      <Box display="flex" alignItems="center" gap={2} mb={2}>
        <Typography sx={{ fontSize: '28px', fontWeight: 800, color: 'var(--gray-text)' }}>
          {t('protocols')}
        </Typography>
        <Box flexGrow={1} />
        {props.editable && (
          <Button
            size="small"
            variant="contained"
            onClick={evaluateNowHandler}
            disabled={evaluating}
            sx={{
              textTransform: 'none',
              borderRadius: 'var(--button-radius)',
              boxShadow: 'none',
              backgroundColor: 'var(--green-primary)',
              '&:hover': { backgroundColor: 'var(--green-dark)', boxShadow: 'none' },
            }}
          >
            {evaluating ? t('evaluating') : t('evaluateNow')}
          </Button>
        )}
      </Box>

      {assigned.length === 0 ? (
        <Typography variant="body2" color="text.secondary" mb={2}>
          {t('noProtocolsForPathologies')}
        </Typography>
      ) : (
        <Table size="small" sx={{ mb: 3 }}>
          <TableBody>
            {assigned.map((a) => (
              <TableRow key={`${a.protocolID}-${a.pathologyID}`}>
                <TableCell>{nameOf(a.protocolID)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Typography variant="subtitle2" mb={1}>
        {t('lastEvaluations')}
      </Typography>

      {evaluations.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t('noEvaluationsYet')}
        </Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('date')}</TableCell>
              <TableCell>{t('result')}</TableCell>
              <TableCell>{t('detail')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {evaluations.map((ev) => (
              <TableRow key={ev.id}>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {new Date(ev.evaluatedAt).toLocaleString()}
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={ev.triggered ? t('triggered') : t('notTriggered')}
                    color={ev.triggered ? 'error' : 'success'}
                  />
                  {ev.triggered && ev.notificationSent && (
                    <Chip size="small" sx={{ ml: 1 }} label={t('notified')} />
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{summariseDetail(ev.details)}</Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Paper>
  )
}
